export type PlayerStatField = {
  key: string;
  label: string;
  type: string;
};

export type PlayerStatPayload = Record<string, string | number>;

const isSupportedField = (
  field: PlayerStatField
): field is PlayerStatField & { type: "string" | "text" | "number" } =>
  Boolean(field.key.trim() && field.label.trim()) &&
  (field.type === "string" || field.type === "text" || field.type === "number");

const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

/**
 * Builds an API-ready player-stat payload using the declared field order.
 */
export const resolvePlayerStatPayload = (
  values: Record<string, unknown>,
  knownFields: ReadonlyArray<PlayerStatField>
): PlayerStatPayload =>
  knownFields.reduce<PlayerStatPayload>((payload, field) => {
    if (!isSupportedField(field)) {
      return payload;
    }

    const value = values[field.key];
    if (isEmptyValue(value)) {
      return payload;
    }

    if (field.type === "number") {
      if (typeof value !== "number" && typeof value !== "string") {
        return payload;
      }

      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        payload[field.key] = numericValue;
      }
      return payload;
    }

    payload[field.key] = String(value);
    return payload;
  }, {});

export const getSupportedPlayerStatFields = (
  fields: ReadonlyArray<PlayerStatField>
) => fields.filter(isSupportedField);

export const readPlayerStatValue = (
  values: Record<string, unknown>,
  key: string
): unknown => {
  if (values[key] !== undefined) {
    return values[key];
  }

  for (const value of Object.values(values)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nestedValue = (value as Record<string, unknown>)[key];
      if (nestedValue !== undefined) return nestedValue;
    }
  }
  return undefined;
};
